// src/components/WarRoomRecord.js
//
// SECTION 532 (2026-09-30, Bob: "There has to be more to the warroom than this" ... "GO"). The business of an
// occasion, drawn inside its card: the agenda items (a claim may be one), the minutes per item, the decisions
// and votes, and the action items. The convener keeps the record while the occasion is open; when he closes
// it the record is locked (the Firestore rules refuse any change to a closed occasion). Invitees read it.
// Every check is src\data\warRoom.js's; this component draws and writes.
import React, { useEffect, useMemo, useState } from 'react';
import DocumentReview from '../components/DocumentReview';   // section 543: spelling, grammar, facts, prose
import { factsFromRecord } from '../data/documentReview';   // section 543
import { db, storage } from '../firebase';
import { ref as sref, uploadBytes, getDownloadURL } from 'firebase/storage';   // section 534: meeting documents
import { collection, query, where, onSnapshot, addDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { agendaItemPatch, minutesPatch, decisionPatch, actionRecord, actionStatusPatch, isOverdue, sortActions, claimLabel,
  GENERAL_ITEM, RESULTS, RESULT_LABEL, ACTION_STATUSES, ACTION_STATUS_LABEL, STATES,
  documentUpload, documentsPatch, followUpPacket, packetText } from '../data/warRoom';   // section 534
import { prepPacket } from '../data/warRoomDrafts';   // Record 657 (War Room item 2)

const C = { line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2', sub: '#10233A' };
const box = { background: C.sub, border: '1px solid ' + C.line, borderRadius: 6, padding: '8px 10px', marginTop: 8 };
const label = { fontSize: 10, color: C.dim, textTransform: 'uppercase', letterSpacing: 1, marginTop: 6 };

// Forensic audit 2026-10-16: which query reads an occasion's action items. Pure, so the test can hold it.
//   administrator                      sessionId == s            (warRoomBuild() in firestore.rules)
//   the committee's General Chairperson committee == c AND sessionId == s - the rule's arm inGCScope(resource.data
//                                      .committee) admits exactly that; he is not always in visibleTo (an item made
//                                      before he was invited, or on an occasion an administrator convened), and the
//                                      old visibleTo query told him "Action items: None." for items he may read
//   everyone else                      visibleTo array-contains uid (the rule's visibleTo arm)
export function occasionActionsQueryKind(s, user) {
  if (!s || !s.id || !user || !user.uid) return null;
  if (user.isAdmin) return 'session';
  if (user.role === 'general_chair' && user.committee && s.committee && user.committee === s.committee) return 'committee';
  return 'visibleTo';
}

// The occasion's action items, for everyone in it (visibleTo), every one for the committee's GC, or every one for the
// administrator. Forensic audit 2026-10-16: returns { rows, error, loaded } - a failed read is NOT "no action items",
// and used to be shown as "None." and "0 open action item(s)" and printed into the follow-up packet as an empty list.
export function useOccasionActions(s, user) {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const kind = occasionActionsQueryKind(s, user);
  useEffect(() => {
    setRows([]); setError(''); setLoaded(false);
    if (!kind) return undefined;
    const col = collection(db, 'warRoomActions');
    const q = kind === 'session' ? query(col, where('sessionId', '==', s.id))
      : kind === 'committee' ? query(col, where('committee', '==', s.committee), where('sessionId', '==', s.id))
      : query(col, where('visibleTo', 'array-contains', user.uid));
    const unsub = onSnapshot(q,
      snap => { setRows(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(a => a.sessionId === s.id)); setError(''); setLoaded(true); },
      e => { setRows([]); setError('Could not read this occasion\'s action items: ' + (e && e.code ? e.code : (e && e.message) || 'refused') + '. This is NOT "none" - what is on record is unknown until it can be read.'); setLoaded(true); });
    return () => unsub();
  }, [kind, s && s.id, s && s.committee, user && user.uid]);   // eslint-disable-line react-hooks/exhaustive-deps
  return { rows, error, loaded };
}

export function ActionRow({ a, user, showOccasion }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const mayChange = user && (a.ownerUid === user.uid || user.isAdmin || (user.role === 'general_chair' && user.committee === a.committee));
  const move = async (status) => {
    setErr('');
    const r = actionStatusPatch(a, user, status, note);
    if (!r.ok) { setErr(r.errors.join(' ')); return; }
    setBusy(true);
    try { await updateDoc(doc(db, 'warRoomActions', a.id), { ...r.patch, statusAt: serverTimestamp() }); setNote(''); }
    catch (e) { setErr('Not saved: ' + (e && e.code ? e.code : e.message)); }
    setBusy(false);
  };
  const late = isOverdue(a);
  return (
    <div style={{ padding: '5px 0', borderTop: '1px solid ' + C.line, fontSize: 12 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <span style={{ color: a.status === 'done' ? C.ok : (late ? C.bad : C.accent), fontWeight: 700, minWidth: 80 }}>{ACTION_STATUS_LABEL[a.status] || a.status}{late ? ' - OVERDUE' : ''}</span>
        <span style={{ color: C.text, flex: 1, minWidth: 160, textDecoration: a.status === 'done' ? 'line-through' : 'none' }}>{a.title}</span>
        <span style={{ color: C.dim }}>{a.ownerName || a.ownerUid}{a.due ? ' · due ' + a.due : ''}</span>
      </div>
      {showOccasion ? <div style={{ color: C.dim, fontSize: 11 }}>From: {a.sessionTitle || 'War Room occasion'}{a.itemTitle ? ' - ' + a.itemTitle : ''}</div> : null}
      {a.statusNote ? <div style={{ color: C.dim, fontSize: 11 }}>Note: {a.statusNote}</div> : null}
      {mayChange ? (
        <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
          <input aria-label="Note (optional)" className="form-input" placeholder="Note (optional)" value={note} onChange={e => setNote(e.target.value)} style={{ flex: 1, minWidth: 140, padding: '3px 6px', fontSize: 12 }} />
          {ACTION_STATUSES.filter(x => x !== a.status).map(x => (
            <button key={x} className="btn btn-secondary btn-sm" disabled={busy} onClick={() => move(x)}>{x === 'done' ? 'Mark done' : x === 'open' ? 'Reopen' : 'In progress'}</button>
          ))}
        </div>
      ) : null}
      {err ? <div style={{ color: C.bad, fontSize: 11 }}>&#9888; {err}</div> : null}
    </div>
  );
}

function ItemBlock({ s, user, item, isConvener, open, write, actions, actionsError }) {
  const id = item ? item.id : GENERAL_ITEM;
  const saved = (s.minutes && s.minutes[id] && s.minutes[id].text) || '';
  const [text, setText] = useState(saved);
  useEffect(() => { setText(saved); }, [saved]);
  const [dec, setDec] = useState({ motion: '', result: 'agreed', vote: false, for: '', against: '', abstain: '' });
  const [act, setAct] = useState({ title: '', ownerUid: '', due: '' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const decisions = (s.decisions || []).filter(d => d && d.itemId === id);
  const mine = sortActions(actions.filter(a => (a.itemId || GENERAL_ITEM) === id));
  const people = [{ uid: s.convenedBy, name: (s.convenedByName || 'Convener') + ' (convener)' }].concat((s.invitees || []).map(x => ({ uid: x.uid, name: x.name || x.uid })));
  const saveDecision = async () => {
    const r = decisionPatch(s, user, { ...dec, itemId: id });
    if (!r.ok) { setErr(r.errors.join(' ')); return; }
    setErr(''); await write(r); setDec({ motion: '', result: 'agreed', vote: false, for: '', against: '', abstain: '' });
  };
  const saveAction = async () => {
    const r = actionRecord(s, user, { ...act, itemId: id });
    if (!r.ok) { setErr(r.errors.join(' ')); return; }
    setErr(''); setBusy(true);
    try { await addDoc(collection(db, 'warRoomActions'), { ...r.record, createdAt: serverTimestamp(), statusAt: serverTimestamp() }); setAct({ title: '', ownerUid: '', due: '' }); }
    catch (e) { setErr('Action item not saved: ' + (e && e.code ? e.code : e.message)); }
    setBusy(false);
  };
  const editing = isConvener && open;
  const empty = !saved && !decisions.length && !mine.length && !actionsError;
  if (!item && !editing && empty) return null;
  return (
    <div style={box}>
      <div style={{ color: C.text, fontSize: 13, fontWeight: 700 }}>
        {item ? item.title : 'General business'}
        {item && item.claimId ? <span style={{ marginLeft: 8, fontSize: 10, color: C.accent, border: '1px solid ' + C.accent, borderRadius: 3, padding: '0 4px' }}>CLAIM ON THE TABLE</span> : null}
      </div>
      <div style={label}>Minutes</div>
      {editing ? (
        <div>
          <textarea aria-label="What was said and done on this item" className="form-textarea" rows={3} value={text} onChange={e => setText(e.target.value)} placeholder="What was said and done on this item" style={{ fontSize: 12 }} />
          {text !== saved ? <button className="btn btn-secondary btn-sm" onClick={() => write(minutesPatch(s, user, id, text))}>Save minutes</button> : null}
        </div>
      ) : <div style={{ fontSize: 12, color: saved ? C.text : C.dim, whiteSpace: 'pre-wrap' }}>{saved || 'No minutes recorded.'}</div>}
      <div style={label}>Decisions</div>
      {decisions.length === 0 ? <div style={{ fontSize: 12, color: C.dim }}>None recorded.</div> : decisions.map(d => (
        <div key={d.id} style={{ fontSize: 12, color: C.text, padding: '2px 0' }}>
          <b style={{ color: d.result === 'failed' ? C.bad : C.ok }}>{RESULT_LABEL[d.result] || d.result}</b> - {d.motion}
          {d.vote && d.tally ? <span style={{ color: C.dim }}> · For {d.tally.for}, against {d.tally.against}, abstain {d.tally.abstain}</span> : null}
          <span style={{ color: C.dim }}> · present: {(d.presentAtVote || []).map(p => p.name).join(', ') || 'none recorded'}</span>
        </div>
      ))}
      {editing ? (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4, alignItems: 'center' }}>
          <input aria-label="Motion or decision" className="form-input" placeholder="Motion or decision" value={dec.motion} onChange={e => setDec({ ...dec, motion: e.target.value })} style={{ flex: 2, minWidth: 180, fontSize: 12 }} />
          <select aria-label="The result of the vote" className="form-input" value={dec.result} onChange={e => setDec({ ...dec, result: e.target.value })} style={{ width: 'auto', fontSize: 12 }}>
            {RESULTS.map(r => <option key={r} value={r}>{RESULT_LABEL[r]}</option>)}
          </select>
          <label style={{ fontSize: 12, color: C.dim }}><input aria-label="Vote taken" type="checkbox" checked={dec.vote} onChange={e => setDec({ ...dec, vote: e.target.checked })} /> vote taken</label>
          {dec.vote ? ['for', 'against', 'abstain'].map(k => (
            <input aria-label={'Votes ' + k} key={k} className="form-input" type="number" min="0" placeholder={k} value={dec[k]} onChange={e => setDec({ ...dec, [k]: e.target.value })} style={{ width: 70, fontSize: 12 }} />
          )) : null}
          <button className="btn btn-secondary btn-sm" onClick={saveDecision}>Record decision</button>
        </div>
      ) : null}
      <div style={label}>Action items</div>
      {/* Forensic audit 2026-10-16: the read failure, never "None.", when the action items could not be read. */}
      {actionsError ? <div style={{ fontSize: 12, color: C.bad }}>&#9888; {actionsError}</div>
        : mine.length === 0 ? <div style={{ fontSize: 12, color: C.dim }}>None.</div> : mine.map(a => <ActionRow key={a.id} a={a} user={user} />)}
      {editing ? (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
          <input aria-label="What is to be done" className="form-input" placeholder="What is to be done" value={act.title} onChange={e => setAct({ ...act, title: e.target.value })} style={{ flex: 2, minWidth: 180, fontSize: 12 }} />
          <select aria-label="Owner uid" className="form-input" value={act.ownerUid} onChange={e => setAct({ ...act, ownerUid: e.target.value })} style={{ width: 'auto', fontSize: 12 }}>
            <option value="">Who owns it</option>
            {people.map(p => <option key={p.uid} value={p.uid}>{p.name}</option>)}
          </select>
          <input aria-label="When this action is due" className="form-input" type="date" value={act.due} onChange={e => setAct({ ...act, due: e.target.value })} style={{ width: 150, fontSize: 12 }} />
          <button className="btn btn-secondary btn-sm" disabled={busy} onClick={saveAction}>Add action item</button>
        </div>
      ) : null}
      {err ? <div style={{ color: C.bad, fontSize: 11, marginTop: 4 }}>&#9888; {err}</div> : null}
    </div>
  );
}

// SECTION 534: the occasion's documents - invitee-only (storage.rules warRoom/), attached by the convener while open.
function Documents({ s, user, isConvener, open, write }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const docs = s.documents || [];
  const attach = async (file) => {
    setErr('');
    const u = documentUpload(s, user, file ? { name: file.name, type: file.type, size: file.size } : null);
    if (!u.ok) { setErr(u.errors.join(' ')); return; }
    setBusy(true);
    try {
      await uploadBytes(sref(storage, u.path), file, u.metadata);
      await write(documentsPatch(s, user, u.entry));
    } catch (e) { setErr('Not attached: ' + (e && e.code ? e.code : e.message)); }
    setBusy(false);
  };
  const openDoc = async (d) => {
    setErr('');
    try { const url = await getDownloadURL(sref(storage, d.path)); window.open(url, '_blank', 'noopener,noreferrer'); }
    catch (e) { setErr('Could not open ' + d.name + ': ' + (e && e.code ? e.code : e.message)); }
  };
  if (!docs.length && !(isConvener && open)) return null;
  return (
    <div style={box}>
      <div style={{ color: C.text, fontSize: 13, fontWeight: 700 }}>Meeting documents <span style={{ fontSize: 11, color: C.dim, fontWeight: 400 }}>- only the people of this occasion can open them</span></div>
      {docs.length === 0 ? <div style={{ fontSize: 12, color: C.dim }}>None attached.</div> : docs.map(d => (
        <div key={d.path} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12, padding: '3px 0', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => openDoc(d)}>Open</button>
          <span style={{ color: C.text }}>{d.name}</span>
          <span style={{ color: C.dim }}>{d.kind}{d.byName ? ' · ' + d.byName : ''}{d.at ? ' · ' + String(d.at).slice(0, 10) : ''}</span>
        </div>
      ))}
      {isConvener && open ? (
        <div style={{ marginTop: 6, fontSize: 12, color: C.dim }}>
          <input aria-label="Attach a document to the record" type="file" disabled={busy} accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt" onChange={e => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) attach(f); }} />
          {busy ? ' Attaching…' : ' PDF, Word, Excel, image or text; up to 25 MB.'}
        </div>
      ) : null}
      {err ? <div style={{ color: C.bad, fontSize: 11 }}>&#9888; {err}</div> : null}
    </div>
  );
}

// Record 657 (Bob 2026-10-12, "All": War Room item 2): the convener's PREP packet - every claim on the agenda with its clocks,
// patterns, carrier record and Board facts, read from the claims she can already read. Hers alone; invitees see titles.
function PrepPacket({ s, claims }) {
  const [open, setOpen] = useState(false);
  const text = useMemo(() => (open ? prepPacket(s, claims || [], new Date().toISOString().slice(0, 10)) : ''), [open, s, claims]);
  const save = () => { try { const u = URL.createObjectURL(new Blob([text], { type: 'text/plain' })); const a = document.createElement('a'); a.href = u; a.download = 'prep-packet-' + (s.scheduledFor || 'occasion') + '.txt'; a.click(); setTimeout(() => URL.revokeObjectURL(u), 1000); } catch (e) { /* on screen */ } };
  return (
    <div style={{ marginTop: 10 }}>
      <button className="btn btn-secondary btn-sm" onClick={() => setOpen(!open)}>{open ? 'Hide the prep packet' : 'Prep packet - the agenda\'s claims, their clocks, patterns and carrier record'}</button>
      {open ? (<div style={{ marginTop: 6 }}><pre style={{ whiteSpace: 'pre-wrap', fontSize: 11.5, color: '#DCE7F2', background: '#0A1628', border: '1px solid #2E4A6B', borderRadius: 6, padding: 10 }}>{text}</pre><button className="btn btn-secondary btn-sm" onClick={save}>Save as a text file</button></div>) : null}
    </div>
  );
}

// SECTION 534: the follow-up packet - the whole record in one piece; print it or save it. At close it is the locked record.
function Packet({ s, actions, actionsError, actionsLoaded }) {
  const [shown, setShown] = useState(false);
  // Forensic audit 2026-10-16: the packet is the record; built from a failed (or not yet landed) read of the action
  // items it would print as a record with no action items. It is not generated until they have been read.
  if (actionsError || !actionsLoaded) {
    return (
      <div style={{ marginTop: 8, fontSize: 12, color: actionsError ? C.bad : C.dim }}>
        {actionsError ? <>&#9888; The follow-up packet is not generated: {actionsError}</> : 'The follow-up packet is generated once the action items have been read…'}
      </div>
    );
  }
  return <PacketBody s={s} actions={actions} shown={shown} setShown={setShown} />;
}

function PacketBody({ s, actions, shown, setShown }) {
  const p = useMemo(() => followUpPacket(s, actions), [s, actions]);
  const text = packetText(p);
  const save = () => {
    try {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
      a.download = 'war-room-' + (s.committee || '') + '-' + (p.date || 'occasion') + '-packet.txt';
      document.body.appendChild(a); a.click(); setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 500);
    } catch (e) { /* the packet is still on screen */ }
  };
  // No window is opened (test-no-blank-popups.mjs: in the officers' Edge --app shortcut a popup is a whole browser
  // window that lands on the new-tab page). The packet prints from a hidden frame; if printing is refused it is saved.
  const [note, setNote] = useState('');
  const print = () => {
    setNote('');
    let frame = null;
    const cleanUp = () => { try { if (frame) frame.remove(); } catch (e) { /* already gone */ } };
    try {
      frame = document.createElement('iframe');
      frame.setAttribute('aria-hidden', 'true');
      frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
      document.body.appendChild(frame);
      const doc = frame.contentWindow.document;
      doc.open();
      doc.write('<!doctype html><html><head><meta charset="utf-8"><title>Follow-up packet</title></head><body></body></html>');
      doc.close();
      const pre = doc.createElement('pre');
      pre.style.cssText = 'font: 12px/1.45 Consolas, monospace; white-space: pre-wrap; margin: 24px;';
      pre.textContent = text;
      doc.body.appendChild(pre);
      setTimeout(() => {
        try { frame.contentWindow.focus(); frame.contentWindow.print(); }
        catch (e) { save(); setNote('Printing was refused, so the packet was saved as a text file instead.'); }
        setTimeout(cleanUp, 60000);
      }, 250);
    } catch (e) { cleanUp(); save(); setNote('Printing was refused, so the packet was saved as a text file instead.'); }
  };
  return (
    <div style={{ marginTop: 8 }}>
      <button className="btn btn-secondary btn-sm" onClick={() => setShown(!shown)}>{shown ? 'Hide the follow-up packet' : (s.state === STATES.CLOSED ? 'Follow-up packet - the locked record' : 'Preview the follow-up packet')}</button>
      {shown ? (
        <div style={box}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <button className="btn btn-secondary btn-sm" onClick={print}>Print</button>
            <button className="btn btn-secondary btn-sm" onClick={save}>Save as text</button>
          </div>
          {note ? <div style={{ color: C.accent, fontSize: 11, marginBottom: 6 }}>{note}</div> : null}
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, color: C.text, margin: 0, fontFamily: 'Consolas, monospace' }}>{text}</pre>
          <DocumentReview title="the follow-up packet" kind="packet" text={text} facts={factsFromRecord(s)} today={new Date().toISOString().slice(0, 10)} />
        </div>
      ) : null}
    </div>
  );
}

export default function WarRoomRecord({ s, user, claims, isConvener, write }) {
  const open = s.state === STATES.CONVENED;
  const { rows: actions, error: actionsError, loaded: actionsLoaded } = useOccasionActions(s, user);   // forensic audit 2026-10-16
  const [title, setTitle] = useState('');
  const [claimId, setClaimId] = useState('');
  const [err, setErr] = useState('');
  const onTable = new Set((s.agendaItems || []).map(x => x && x.claimId).filter(Boolean));
  const pickable = useMemo(() => (claims || []).filter(c => c && !onTable.has(String(c.docId || c.id))), [claims, (s.agendaItems || []).length]);
  const addItem = async () => {
    const claim = claimId ? (claims || []).find(c => String(c.docId || c.id) === claimId) : null;
    const r = agendaItemPatch(s, user, { title, claim });
    if (!r.ok) { setErr(r.errors.join(' ')); return; }
    setErr(''); await write(r); setTitle(''); setClaimId('');
  };
  const items = s.agendaItems || [];
  const openActions = actions.filter(a => a.status !== 'done').length;
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 11, color: C.dim, textTransform: 'uppercase', letterSpacing: 1 }}>
        The business - {items.length} item(s), {(s.decisions || []).length} decision(s), {actionsError ? 'action items unreadable' : (actionsLoaded ? openActions : '…') + ' open action item(s)'}{open ? '' : ' · LOCKED at close'}
      </div>
      {items.map(it => <ItemBlock key={it.id} s={s} user={user} item={it} isConvener={isConvener} open={open} write={write} actions={actions} actionsError={actionsError} />)}
      <ItemBlock s={s} user={user} item={null} isConvener={isConvener} open={open} write={write} actions={actions} actionsError={actionsError} />
      <Documents s={s} user={user} isConvener={isConvener} open={open} write={write} />
      {isConvener && open ? (
        <div style={{ ...box, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <input aria-label="Add an agenda item" className="form-input" placeholder="Add an agenda item" value={title} onChange={e => setTitle(e.target.value)} style={{ flex: 2, minWidth: 180, fontSize: 12 }} />
          {claims ? (
            <select aria-label="Claim id" className="form-input" value={claimId} onChange={e => setClaimId(e.target.value)} style={{ flex: 1, minWidth: 180, width: 'auto', fontSize: 12 }}>
              <option value="">{pickable.length ? '...or put a claim on the table' : 'No claim to add'}</option>
              {pickable.map(c => { const k = String(c.docId || c.id); return <option key={k} value={k}>{claimLabel(c)}</option>; })}
            </select>
          ) : null}
          <button className="btn btn-secondary btn-sm" onClick={addItem}>Add to agenda</button>
          {err ? <div style={{ color: C.bad, fontSize: 11, width: '100%' }}>&#9888; {err}</div> : null}
        </div>
      ) : null}
      {isConvener && open ? <div style={{ fontSize: 11, color: C.dim, marginTop: 6 }}>Closing the occasion locks the agenda, minutes and decisions as the record. Action items stay live until done. Every invitee then has the follow-up packet on his desk.</div> : null}
      <Packet s={s} actions={actions} actionsError={actionsError} actionsLoaded={actionsLoaded} />
      {isConvener ? <PrepPacket s={s} claims={claims} /> : null}
    </div>
  );
}
